import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// Global billing_config editor (BOO-31). Values here move real money —
// markups, Railway rates, charging gates — so edits are per-row and
// explicit (no bulk save). New keys still ship via migration/seed; the
// backend rejects unknown keys so a typo can't create a shadow config.
export function AdminBillingConfigPage() {
  useDocumentTitle("Admin: Billing config");
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-billing-config"],
    queryFn: api.admin.billingConfig,
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const update = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.admin.billingConfigUpdate(key, value),
    onSuccess: (_res, { key }) => {
      toast.success(`${key} saved — takes effect within a minute (60s config cache)`);
      setDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["admin-billing-config"] });
    },
  });

  if (isLoading) return <LoadingIndicator />;
  if (isError || !data) return <ErrorComponent error={error ?? "Failed to load config"} />;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Billing config</h1>
        <p className="text-sm text-muted-foreground">
          Global markups, rates, and charging gates. Per-org markup overrides
          live on each organization's billing tab. Changes apply within a
          minute (60-second config cache on the backend).
        </p>
      </div>

      {update.isError && <InlineError error={update.error ?? "Update failed"} />}

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Key</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Value</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.data.map((row) => {
              const draft = drafts[row.key];
              const dirty = draft !== undefined && draft !== row.value;
              return (
                <tr key={row.key} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{row.key}</td>
                  <td className="px-3 py-2 text-muted-foreground">{row.description ?? "—"}</td>
                  <td className="px-3 py-2">
                    <Input
                      className="h-8 w-40 font-mono text-xs"
                      value={draft ?? row.value}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [row.key]: e.target.value }))
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    {formatDate(row.updatedAt)}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      disabled={!dirty || update.isPending}
                      onClick={() => update.mutate({ key: row.key, value: draft!.trim() })}
                    >
                      Save
                    </Button>
                  </td>
                </tr>
              );
            })}
            {data.data.length === 0 && (
              <tr className="border-t">
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No config rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
